import { useCallback, useState } from "react";
import { type DlqEntrySummary, fetchDlqEntries } from "../../api/admin-dlq";
import { t } from "../../i18n";
import { DLQ_PAGE_SIZE } from "./dlq-constants";

export type DlqFilters = {
  readonly originQueue: string;
  readonly fromDate: string;
  readonly toDate: string;
};

export type DlqListState = {
  readonly items: ReadonlyArray<DlqEntrySummary>;
  readonly cursor: string | null;
  readonly searched: boolean;
  readonly loading: boolean;
  readonly error: string | null;
  readonly expandedKey: string | null;
  readonly setExpandedKey: (key: string | null) => void;
  readonly load: (cursor?: string | null) => Promise<void>;
};

export const INITIAL_DLQ_FILTERS: DlqFilters = {
  originQueue: "audit-write",
  fromDate: "",
  toDate: "",
};

export function useDlqList(filters: DlqFilters): DlqListState {
  const [items, setItems] = useState<ReadonlyArray<DlqEntrySummary>>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const load = useCallback(
    async (nextCursor: string | null = null): Promise<void> => {
      setLoading(true);
      setError(null);
      setSearched(true);
      try {
        const result = await fetchDlqEntries({
          origin_queue: filters.originQueue,
          from_date: filters.fromDate || undefined,
          to_date: filters.toDate || undefined,
          cursor: nextCursor ?? undefined,
          limit: DLQ_PAGE_SIZE,
        });
        setItems(result.items);
        setCursor(result.cursor);
        setExpandedKey(null);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t("common.failed_to_load"),
        );
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  return {
    items,
    cursor,
    searched,
    loading,
    error,
    expandedKey,
    setExpandedKey,
    load,
  };
}
